import { Type, type Static } from "@sinclair/typebox";
export const InvoiceDestinationSchema = Type.Union([
  Type.Literal("structure"),
  Type.Literal("job"),
  Type.Literal("mixed"),
  Type.Literal("review"),
]);
export type InvoiceDestination = Static<typeof InvoiceDestinationSchema>;
export const InvoiceStructureCategorySchema = Type.Union([
  Type.Literal("carburanti"),
  Type.Literal("assicurazioni"),
  Type.Literal("telepass"),
  Type.Literal("assicurazioni-mezzi"),
  Type.Literal("assicurazioni-rc-azienda"),
  Type.Literal("visite-mediche"),
  Type.Literal("formazione"),
  Type.Literal("marketing"),
  Type.Literal("dpi"),
  Type.Literal("fatture-in-cloud"),
  Type.Literal("aruba"),
  Type.Literal("tim"),
]);
export type InvoiceStructureCategory = Static<
  typeof InvoiceStructureCategorySchema
>;
export const invoiceStructureCategories = [
  { key: "carburanti", label: "Carburanti" },
  { key: "visite-mediche", label: "Visite mediche" },
  { key: "formazione", label: "Formazione" },
  { key: "assicurazioni", label: "Assicurazioni" },
  { key: "assicurazioni-mezzi", label: "Assicurazioni mezzi" },
  { key: "assicurazioni-rc-azienda", label: "RC azienda" },
  { key: "telepass", label: "Telepass" },
  { key: "marketing", label: "Marketing" },
  { key: "dpi", label: "DPI" },
  { key: "fatture-in-cloud", label: "Fatture in Cloud" },
  { key: "aruba", label: "Aruba" },
  { key: "tim", label: "Telefonia" },
] as const satisfies readonly {
  key: InvoiceStructureCategory;
  label: string;
}[];
export const invoiceJobCategories = [
  { key: "materiali", label: "Materiali" },
  { key: "servizi-noleggi", label: "Servizi e noleggi" },
  { key: "subappalti", label: "Subappalti" },
  { key: "costi-commessa", label: "Da dettagliare" },
] as const;
export const InvoiceCostCategorySchema = Type.Union([
  InvoiceStructureCategorySchema,
  Type.Literal("materiali"),
  Type.Literal("servizi-noleggi"),
  Type.Literal("subappalti"),
  Type.Literal("costi-commessa"),
]);
export type InvoiceCostCategory = Static<typeof InvoiceCostCategorySchema>;
export interface InvoiceCostDecision {
  version: 1;
  destination: InvoiceDestination;
  category: InvoiceCostCategory | null;
  reason: string;
  source: "original-xml" | "unavailable" | "operator-rule";
  ruleId?: string;
  ruleVersion?: string;
  lines: {
    number: string;
    description: string;
    amount: string;
    destination: InvoiceDestination;
    category?: InvoiceCostCategory | null;
  }[];
}
/** Actual persisted posting, distinct from the supplier's destination. */
export interface StructureInvoiceReception {
  status: "posted" | "held" | "missing-center" | "ineligible" | "pending";
  reason: string;
  costCenterId: string | null;
  costCenterName: string | null;
}
export interface JobInvoiceReception {
  status: "posted" | "pending" | "held";
  jobId: string | null;
  jobCode: string | null;
  reason: string;
  fingerprint: string;
}
export interface InvoiceJobChoice {
  id: string;
  code: string;
  title: string;
  aliases: string[];
}
export interface CatalogInvoiceCost {
  automatic?: boolean;
  jobReception?: JobInvoiceReception;
  id: string;
  invoiceNumber: string | null;
  date: string;
  supplier: string;
  netAmount: string;
  currency: string;
  decision: InvoiceCostDecision;
  acquisitionWarning?: string | null;
  structureReception?: StructureInvoiceReception;
}
export const CatalogDetailFilterProperties = {
  search: Type.Optional(Type.String({ maxLength: 200 })),
  supplierId: Type.Optional(Type.String({ format: "uuid" })),
  sectorId: Type.Optional(Type.String({ format: "uuid" })),
  dateFrom: Type.Optional(Type.String({ format: "date" })),
  dateTo: Type.Optional(Type.String({ format: "date" })),
};
export const InvoiceCostsQuerySchema = Type.Object(
  {
    ...CatalogDetailFilterProperties,
    managementYear: Type.Optional(
      Type.Integer({ minimum: 2000, maximum: 2200 }),
    ),
    destination: Type.Optional(InvoiceDestinationSchema),
    queue: Type.Optional(
      Type.Union([Type.Literal("pending"), Type.Literal("acquired")]),
    ),
    category: Type.Optional(InvoiceCostCategorySchema),
    limit: Type.Optional(
      Type.Integer({ minimum: 1, maximum: 200, default: 100 }),
    ),
    offset: Type.Optional(Type.Integer({ minimum: 0, default: 0 })),
  },
  { additionalProperties: false },
);
export type InvoiceCostsQuery = Static<typeof InvoiceCostsQuerySchema>;
export interface CatalogInvoiceCostsPage {
  managementYear: number;
  items: CatalogInvoiceCost[];
  nextOffset: number | null;
  counts: Record<InvoiceDestination, number>;
  /** Signed net EUR totals for the selected year and queue/history, independent of category and pagination. */
  totals: Record<
    InvoiceDestination,
    { netAmount: string; excludedCurrencyCount: number }
  >;
  categoryCounts: Partial<Record<InvoiceCostCategory, number>>;
}

export type CatalogInvoiceVerificationDayStatus =
  "verified" | "running" | "failed" | "not-recorded";

export interface CatalogInvoiceVerificationDay {
  date: string;
  status: CatalogInvoiceVerificationDayStatus;
  checkedAt: string | null;
  documentCount: number | null;
}

/** Read-only evidence of the daily FIC received-invoice acquisition. */
export interface CatalogInvoiceVerification {
  companyConnected: boolean;
  automaticChecksEnabled: boolean;
  today: string;
  todayStatus: CatalogInvoiceVerificationDayStatus;
  lastAttemptAt: string | null;
  lastVerifiedAt: string | null;
  nextAttemptAt: string | null;
  message: string | null;
  days: CatalogInvoiceVerificationDay[];
}

export const InvoiceStructurePreviewSchema = Type.Object(
  {
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
    mappings: Type.Array(
      Type.Object(
        {
          category: InvoiceStructureCategorySchema,
          costCenterId: Type.String({ format: "uuid" }),
        },
        { additionalProperties: false },
      ),
      { minItems: 1, maxItems: 12 },
    ),
  },
  { additionalProperties: false },
);
export type InvoiceStructurePreviewInput = Static<
  typeof InvoiceStructurePreviewSchema
>;
export const InvoiceStructurePostSchema = Type.Object(
  {
    managementYear: Type.Optional(
      Type.Integer({ minimum: 2000, maximum: 2200 }),
    ),
    operationId: Type.String({ format: "uuid" }),
    confirmed: Type.Literal(true),
    invoices: Type.Array(
      Type.Object(
        {
          id: Type.String({ format: "uuid" }),
          fingerprint: Type.String({ pattern: "^[a-f0-9]{64}$" }),
          costCenterId: Type.String({ format: "uuid" }),
        },
        { additionalProperties: false },
      ),
      { minItems: 1, maxItems: 1000 },
    ),
  },
  { additionalProperties: false },
);
export type InvoiceStructurePost = Static<typeof InvoiceStructurePostSchema>;
export interface InvoiceStructurePreview {
  invoices: {
    id: string;
    fingerprint: string;
    costCenterId: string;
    costCenterName: string;
    date: string;
    supplier: string;
    invoiceNumber: string | null;
    amount: string;
  }[];
  skipped: {
    id: string;
    reason: string;
    date: string;
    invoiceNumber: string | null;
    supplier: string;
  }[];
  total: string;
}

/** Complete yearly FiC actuals replace existing monetary actuals; labor and budgets are retained. */
export const InvoiceStructureReplacementPreviewSchema = Type.Object(
  {
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
    mappings: Type.Optional(
      Type.Array(
        Type.Object(
          {
            category: InvoiceStructureCategorySchema,
            costCenterId: Type.String({ format: "uuid" }),
          },
          { additionalProperties: false },
        ),
        { maxItems: 12 },
      ),
    ),
  },
  { additionalProperties: false },
);
export type InvoiceStructureReplacementPreviewInput = Static<
  typeof InvoiceStructureReplacementPreviewSchema
>;
export const InvoiceStructureReplacementPostSchema = Type.Object(
  {
    ...InvoiceStructureReplacementPreviewSchema.properties,
    operationId: Type.String({ format: "uuid" }),
    confirmed: Type.Literal(true),
    expectedFingerprint: Type.String({ pattern: "^[a-f0-9]{64}$" }),
  },
  { additionalProperties: false },
);
export type InvoiceStructureReplacementPost = Static<
  typeof InvoiceStructureReplacementPostSchema
>;
export type InvoiceStructureReplacementInvoice =
  InvoiceStructurePreview["invoices"][number] & {
    category: InvoiceStructureCategory;
  };
export interface InvoiceStructureReplacementPreview {
  managementYear: number;
  fingerprint: string;
  associations: {
    category: InvoiceStructureCategory;
    label: string;
    invoiceCount: number;
    amount: string;
    status: "automatic" | "selected" | "missing" | "ambiguous" | "blocked";
    costCenterId: string | null;
    costCenterName: string | null;
    reason: string | null;
    candidates: { id: string; name: string }[];
  }[];
  centers: {
    costCenterId: string;
    costCenterName: string;
    categories: InvoiceStructureCategory[];
    previousAmount: string;
    ficAmount: string;
    laborAmount: string;
    nextAmount: string;
    difference: string;
    budgetAmount: string;
    invoiceCount: number;
  }[];
  invoices: InvoiceStructureReplacementInvoice[];
  skipped: InvoiceStructurePreview["skipped"];
  total: string;
}
export interface InternalCenterFicActual {
  amount: string;
  updatedAt: string;
  invoices: InvoiceStructureReplacementInvoice[];
}
